import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, VFC } from 'react';

import { useNotifications } from '@/apis/aniftyBackend/notifications/getNotifications';
import { useMarkNotificationsAsRead } from '@/apis/aniftyBackend/notifications/markNotificationsAsRead';
import DefaultSpinner from '@/components/DefaultSpinner';
import ProcessResultAlert from '@/components/ProcessResultAlert';

import NotificationItem from './NotificationItem';
import styles from './NotificationsPage.module.scss';

const NotificationsPage: VFC = () => {
  const { t } = useTranslation();

  const { data: notificationPages, status } = useNotifications({
    itemsPerPage: 20,
  });

  const notifications = notificationPages?.pages.flat() || [];

  const readNotifications = useMemo(
    () => notifications.filter((n) => !!n.openedAt),
    [notifications]
  );

  const unreadNotifications = useMemo(
    () => notifications.filter((n) => !n.openedAt),
    [notifications]
  );

  const { mutateAsync: markNotificationsAsRead } = useMarkNotificationsAsRead();

  useEffect(() => {
    return () => {
      // Mark all notifications as read on page leave
      if (unreadNotifications.length) {
        markNotificationsAsRead({ notificationIDs: 'all' });
      }
    };
  }, []);

  return (
    <div className={styles.notificationsPageContainer}>
      <h1>{t('notificationsPage.title')}</h1>

      {status === 'loading' && <DefaultSpinner />}

      {status === 'error' && (
        <ProcessResultAlert
          status="fail"
          failMessage="Failed to load notifications"
        />
      )}

      {status === 'success' && (
        <>
          <div className={styles.notificationListContainer}>
            <div className={styles.unreadLabel}>
              <p>{t('notificationsPage.unread')}</p>
            </div>

            {unreadNotifications.length ? (
              <div className={styles.notificationList}>
                {unreadNotifications.map((notification, index) => (
                  <NotificationItem key={index} notification={notification} />
                ))}
              </div>
            ) : (
              t('notificationsPage.noUnread')
            )}
          </div>

          <div className={styles.notificationListContainer}>
            <p className={styles.readLabel}>{t('notificationsPage.read')}</p>

            {readNotifications.length ? (
              <div
                className={`${styles.notificationList} ${styles.readNotificationList}`}
              >
                {readNotifications.map((notification, index) => (
                  <NotificationItem key={index} notification={notification} />
                ))}
              </div>
            ) : (
              t('notificationsPage.noRead')
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsPage;
